import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import Dialog from "components/dialog";
import DeleteIcon from "components/icons/DeleteIcon";
import { useState } from "react";
import toast from "react-hot-toast";
import { FiUploadCloud } from "react-icons/fi";
import { getSubdomain } from "api";

const UploadCsvDialog = ({ openCsvDialog, setOpenCsvDialog, getUsers }) => {
  const [uploadedFile, setUploadedFile] = useState(null);

  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      toast.error("Please select a file before uploading.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", uploadedFile);
    const subdomain = getSubdomain();
    if (subdomain) {
      formData.append("x-church-slug", subdomain);
    }

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    try {
      await toast.promise(
        AtriumApi.post("/v1/read-data-from-csv", formData, config),
        {
          loading: "Uploading data...",
          // success: "Data uploaded successfully.",
          success: (res) => res?.data?.message || "Data uploaded successfully.",
        },
        {
          position: "top-center",
        }
      );

      setOpenCsvDialog(false);
      await getUsers();
    } catch (error) {
      console.error("Error uploading data:", error);
      const message =
        error?.response?.data?.errors?.length > 0
          ? error?.response?.data?.errors?.join(", ")
          : error?.response?.data?.message || "Something went wrong";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      isOpen={openCsvDialog}
      onClose={() => setOpenCsvDialog(false)}
      placement="top"
    >
      <div className="bg-[#F4F7FE] dark:bg-navy-900 p-6 rounded-2xl text-center space-y-3 mt-10">
        <div className="text-4xl text-[#A3AED0] flex justify-center">
          <FiUploadCloud />
        </div>
        <h2 className="font-semibold text-sm text-[#1B2559] dark:text-white">
          Upload CSV File (Records should be less than 950)
        </h2>
        <p className="text-[#A3AED0] text-sm">
          Only .csv files are supported
          <br />
          Max file size 50MB
        </p>

        <div className="text-[#A3AED0] text-sm">
          <label
            htmlFor="csvUpload"
            className="text-[#4318FF] underline cursor-pointer"
          >
            Browse
          </label>
          <input
            id="csvUpload"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>
      {uploadedFile && (
        <div className="flex justify-between items-center bg-[#F4F7FE] dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm mt-4">
          <div className="text-left text-sm">
            <p className="text-[#1B2559] dark:text-white font-medium">
              {uploadedFile.name}
            </p>
            <p className="text-[#A3AED0]">
              {(uploadedFile.size / 1024).toFixed(2)} KB
            </p>
          </div>
          <button onClick={() => setUploadedFile(null)} title="Remove file">
            <DeleteIcon color="red" cursor="pointer" />
          </button>
        </div>
      )}

      <div className="flex flex-row justify-center gap-4 mt-6 mb-3">
        <DarkButton
          kind="success"
          onClick={handleUpload}
          disabled={!uploadedFile || uploading}
        >
          {uploading ? "Uploading..." : "Import"}
        </DarkButton>
        <GrayButton onClick={() => setOpenCsvDialog(false)}>Cancel</GrayButton>
      </div>

      <div className="text-center mt-4">
        <button
          onClick={() => {
            const link = document.createElement("a");
            link.href = "/user-template.csv"; // Make sure this file exists in the public folder
            link.download = "user-template.csv";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          className="text-sm text-[#4318FF] underline hover:text-indigo-600"
        >
          Download CSV Template
        </button>
      </div>
    </Dialog>
  );
};

export default UploadCsvDialog;
